// src/pages/Login/index.jsx
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Divider } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const BRAND_PURPLE = '#6B21A8';
const BRAND_ORANGE = '#F97316';

// Иконка Google
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 8, verticalAlign: 'middle' }}>
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
  </svg>
);

export default function LoginPage({ onLogin }) {
  const [loading, setLoading] = useState(false);

  // Показываем ошибку если редирект от Google пришёл с ошибкой
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const errCode = params.get('error');
    if (errCode) {
      const errorMessages = {
        wrong_domain:        'Доступ только для корпоративной почты',
        not_allowed:         'Ваш аккаунт не имеет доступа. Обратитесь к администратору',
        google_cancelled:    'Вход через Google отменён',
        google_token_failed: 'Ошибка авторизации Google. Попробуйте ещё раз',
      };
      message.error(errorMessages[errCode] || 'Ошибка входа');
      // Убираем параметр из URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', values.login);
      formData.append('password', values.password);
      const res = await axios.post(`${API_BASE_URL}/auth/token`, formData);
      const { access_token, login, full_name, is_admin, is_super_admin, is_security, cfo_id } = res.data;
      localStorage.setItem('token',          access_token);
      localStorage.setItem('login',          login);
      localStorage.setItem('full_name',      full_name || '');
      localStorage.setItem('is_admin',       String(is_admin       ?? false));
      localStorage.setItem('is_super_admin', String(is_super_admin ?? false));
      localStorage.setItem('is_security',    String(is_security    ?? false));
      localStorage.setItem('cfo_id',         cfo_id != null ? String(cfo_id) : '');
      onLogin(access_token, login);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Неверный логин или пароль');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google/login`;
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center', background: '#f5f5f5',
    }}>
      <Card style={{ width: 380, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <svg width="40" height="40" viewBox="0 0 28 28" fill="none" style={{ marginBottom: 8 }}>
            <path d="M14 2C14 2 8 8 8 14.5C8 18.09 10.69 21 14 21C17.31 21 20 18.09 20 14.5C20 12 18.5 10 17 8.5C17 8.5 16.5 11 15 12C15 12 16 9 14 2Z" fill={BRAND_ORANGE} />
            <path d="M14 21C11.5 21 10 18.5 10 16.5C10 15 10.8 13.8 12 13C12 13 11.8 14.5 13 15.5C13 15.5 13 14 14 13C14 13 15 15 14.5 16.5C14.5 16.5 16 15.5 16 14C16 14 17.5 15 17.5 16.5C17.5 18.5 16 21 14 21Z" fill="#FCD34D" />
          </svg>
          <div style={{ fontWeight: 800, fontSize: 18, color: BRAND_PURPLE, letterSpacing: 1 }}>
            SPORTSPIRED
          </div>
          <div style={{ fontWeight: 600, fontSize: 12, color: BRAND_ORANGE, letterSpacing: 2 }}>
            GAMES
          </div>
          <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>IT Assets</div>
        </div>

        {/* Кнопка Google */}
        <Button
          block
          size="large"
          onClick={handleGoogleLogin}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid #d9d9d9', background: '#fff',
            color: '#333', fontWeight: 500, marginBottom: 4,
          }}
        >
          <GoogleIcon />
          Войти через Google
        </Button>

        <Divider style={{ color: '#aaa', fontSize: 12 }}>или</Divider>

        <Form layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="login" rules={[{ required: true, message: 'Введите логин' }]}>
            <Input prefix={<UserOutlined />} placeholder="Логин" size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Введите пароль' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Пароль" size="large" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={loading}
            style={{ background: BRAND_PURPLE, borderColor: BRAND_PURPLE }}>
            Войти
          </Button>
        </Form>
      </Card>
    </div>
  );
}